import { v4 as uuidv4 } from 'uuid'
import { IAnalyticsServiceConfiguration } from '../..'
import { GlobalDataFormatter } from '../../../../domain/analytics/providers/global-data'
import { sha256 } from '../../../../utils/common/hash-code'
import { DOMUtils } from '../../../../utils/dom'
import { IdentityEvent } from '../../../common/events/identity'
import { PermutiveKeysMapping, PermutivePageviewAttributes } from '../../formatters/permutive-keys'

declare let window: any
const READY_STATE = 4
const HTTP_CODE_SUCCESS = 200
export class Permutive {
    public url: string
    public request: XMLHttpRequest
    /**
     * Initialize Permutive
     */
    public init(organizationId: string, workspaceId: string, workspaceApiKey: string): void {
        if (!window.permutive && organizationId && workspaceId && workspaceApiKey) {
            const permutive = window.permutive || {}
            permutive.q= []
            permutive.config= {}
            permutive.config.apiKey= workspaceApiKey
            permutive.config.workspaceId=workspaceId
            permutive.config.viewId = uuidv4()
            permutive.config.environment = 'production'
            this.url = 'https://api.permutive.com/ctx/v1/segment?k=' + workspaceApiKey
            this.request = new window.XMLHttpRequest()

            const o = ['addon', 'identify', 'track', 'trigger', 'query', 'segment',
                 'segments', 'ready', 'on', 'once', 'user', 'consent']
            for (const r of o) {
                permutive[r] = (...args: any[]): void => {
                    const i = Array.prototype.slice.call(args, 0)
                    permutive.q.push({
                        arguments: i,
                        functionName: r
                    })
                }
            }
            window.permutive = permutive
            // send mp_id to Permutive
            addEventListener(IdentityEvent.MP_READY, (e: any) => {
                if (e.detail.mpId) {
                    window.permutive.identify([{
                        id: e.detail.mpId,
                        tag: 'mpId'
                    }])
                }
            })
            // send hashed email to Permutive
            addEventListener(IdentityEvent.IDENTITY_INIT, (e: any) => {
                /* istanbul ignore next */
                if (e.detail.email) {
                    sha256(e.detail.email)
                        .then((emailSha256) => {
                            window.permutive.identify([{
                                id: emailSha256,
                                tag: 'email_sha256'
                            }])
                        })
                        .catch((error: any) => {
                            console.error('email_sha256 error', error)
                        })
                }
            })
        }
    }

    public getContextualSegment(pageProperties: {}) {
        /**
         * Api request to get permutive contextual segment
         */
        this.request.open('POST', this.url, true)
        this.request.onreadystatechange = ()=> {

            if (this.request.readyState === READY_STATE && this.request.status === HTTP_CODE_SUCCESS) {
                try {
                    const apiResponse = JSON.parse(this.request.responseText)
                    // expose API response to main SDK
                    window.permutive.addon('contextual', apiResponse)

                    window.googletag = window.googletag || {}
                    window.googletag.cmd = window.googletag.cmd || []
                    window.googletag.cmd.push(() => {
                        /* istanbul ignore next */
                        const pubads = window.googletag.pubads()
                        const ctxcohorts = apiResponse.gam || []
                        const targetingKey = 'prmtvctx'
                        const targetingValues = ctxcohorts.concat('rts') // always include "rts"

                        pubads.setTargeting(targetingKey, targetingValues)
                    })
                } catch (e) {
                    return
                }
            }
        }
        this.request.send(JSON.stringify({
            pageProperties: this.enrichContextualClient(pageProperties),
            url: document.URL
        }))
    }

    public sendPageViewEvent(metaProperties?: IAnalyticsServiceConfiguration) {
        /**
         * Send Page View event
         */
        if (window.permutive) {
            const eventdata = this.getEventData(metaProperties, PermutivePageviewAttributes)
            this.getContextualSegment(eventdata)
            // Add custom event to pageview event
            window.permutive.addon('web', {
                page:{...eventdata}
            })
            const workspaceId = window.permutive.config.workspaceId
            // Load permutive script
            DOMUtils.loadScript(`https://${workspaceId}.edge.permutive.app/${workspaceId}-web.js`)

            // add permutive to googletag
            window.googletag=window.googletag||{}
            window.googletag.cmd=window.googletag.cmd||[]
            /* istanbul ignore next */
            window.googletag.cmd.push((): void => {
                if(0===window.googletag.pubads().getTargeting('permutive').length) {
                    const e=window.localStorage.getItem('_pdfps')
                    window.googletag.pubads().setTargeting('permutive',e?JSON.parse(e):[])
                    const permutiveId=window.localStorage.getItem('permutive-id')
                    if (permutiveId) {
                        window.googletag.pubads().setTargeting('puid',permutiveId)
                        window.googletag.pubads().setTargeting('ptime',Date.now().toString())
                    }
                    if (window.permutive.config.viewId) {
                        window.googletag.pubads().setTargeting('prmtvvid',window.permutive.config.viewId)
                    }
                    if (window.permutive.config.workspaceId) {
                        window.googletag.pubads().setTargeting('prmtvwid',window.permutive.config.workspaceId)
                    }
                }
            })
        }
    }
    // send events with attributes to Permutive
    public sendEvent(eventType: string,eventData: any) {
        if(window.permutive) {
            window.permutive.track(eventType, eventData)
        }
    }
    public getEventData( metaProperties: any, dataAttrs: string[], commonData?: {}, customData?: {}): any {
        /**
         *  get Event key/value data
         */
        let eventData: any = { ...commonData}  // extra data common to all providers

        // Get Data Values ( this.metaProperties this is data coming from page-data )
        const globalFormatter = new GlobalDataFormatter(metaProperties)
        dataAttrs.forEach((attr) => {
            try {
                const attrValue = globalFormatter.lookup(attr)
                if(attrValue !== '' && attrValue.length > 0) {
                    eventData[attr] = attrValue
                }
            } catch(e) {
                console.error('GlobalDataFormatter error in tags.Permutive', e)
            }
        })

        if(customData) {
            eventData = {
                ...eventData,
                ...customData
            }
        }

        // Update keys names
        for(const mappedKey in PermutiveKeysMapping) {
            if (mappedKey in eventData) {
                eventData[PermutiveKeysMapping[mappedKey]] = eventData[mappedKey]
                delete eventData[mappedKey]
            }
        }
        return eventData
    }
    private getContextualClient() {
        /**
         * Create Contextual client
         */
        return {
            domain: window.location.hostname,
            referrer: document.referrer,
            title: document.title,
            type: 'web',
            url: document.URL,
            user_agent: navigator.userAgent
          }
    }
    private enrichContextualClient(pageProps: any): any {
        /**
         * Create contextual enrich client
         */
        try {
            const clientObject = {
                client: this.getContextualClient()
            }

            if (typeof pageProps === 'object' && !Array.isArray(pageProps)) {
                return Object.assign({}, pageProps, clientObject)
            } else {
                return clientObject
            }
        } catch (e) {
            return {}
        }
    }

}
